import axios from "axios";
import { useEffect, useState } from "react";
import { Card, Row, Col, Form, Alert } from "react-bootstrap";
import { useForm, useWatch } from "react-hook-form";
import BASE_URL from "../../../../assets/global/url";
import {
  formatToPHP,
  formatToTwoDecimal,
} from "../../../../utils/numberFormatter";

const IssuedCheckModalBody = ({ rowData, onSave, onBalanceChange }) => {
  const [accountData, setAccountData] = useState([]);
  const [selectedAccountBalance, setSelectedAccountBalance] = useState(null);

  const {
    register,
    control,
    setValue,
    handleSubmit,
    formState: { errors },
  } = useForm({
    defaultValues: {
      account1: "",
      account2: "",
      account3: "",
    },
    mode: "onChange",
  });

  const [selectedAccount1, selectedAccount2, selectedAccount3] = useWatch({
    control,
    name: ["account1", "account2", "account3"],
  });

  const onSubmit = (data) => {
    const selectedAccountObj = accountData.find(
      (item) => item.id === data.account3,
    );

    const currencyName = selectedAccountObj?.currency?.currency_name || "PHP";
    const currencyRate = selectedAccountObj?.currency?.currency_rate || 1;
    const accountBalance = selectedAccountObj?.amount ?? 0;

    onSave({
      ...data,
      currencyName,
      currencyRate,
      accountBalance,
    });
  };

  const fetchAccount = async () => {
    try {
      const response = await axios.get(
        `${BASE_URL}/issuedCheck/getAccountListSub3`,
      );
      if (response.data) {
        setAccountData(response.data);
      }
    } catch (error) {
      console.error("Error fetching accounts: " + error);
    }
  };

  useEffect(() => {
    fetchAccount();
  }, []);

  useEffect(() => {
    setValue("account2", "");
    setValue("account3", "");
    setSelectedAccountBalance(null);
    onBalanceChange?.(false);
  }, [selectedAccount1, setValue]);

  useEffect(() => {
    setValue("account3", "");
    setSelectedAccountBalance(null);
    onBalanceChange?.(false);
  }, [selectedAccount2, setValue]);

  // Update balance when account3 changes
  useEffect(() => {
    if (selectedAccount3) {
      const found = accountData.find((item) => item.id === selectedAccount3);
      const balance = found?.amount ?? null;
      setSelectedAccountBalance(balance);
      // Notify parent whether balance is insufficient
      if (balance !== null) {
        onBalanceChange?.(balance < (rowData?.amount ?? 0));
      } else {
        onBalanceChange?.(false);
      }
    } else {
      setSelectedAccountBalance(null);
      onBalanceChange?.(false);
    }
  }, [selectedAccount3, accountData]);

  const account1Options = [
    ...new Set(
      accountData
        .map((item) => item.account_list_base_sub?.module_type)
        .filter(Boolean),
    ),
  ];

  const account2Options = Object.values(
    accountData
      .filter(
        (item) => item.account_list_base_sub?.module_type === selectedAccount1,
      )
      .reduce((acc, item) => {
        const baseSub = item.account_list_base_sub;
        if (baseSub && !acc[baseSub.id]) {
          acc[baseSub.id] = { id: baseSub.id, name: baseSub.subject_name };
        }
        return acc;
      }, {}),
  );

  const account3Options = accountData
    .filter((item) => item.account_list_base_sub?.id === selectedAccount2)
    .map((item) => ({ id: item.id, name: item.account_name }));

  const checkAmount = rowData?.amount ?? 0;
  const isInsufficient =
    selectedAccountBalance !== null && selectedAccountBalance < checkAmount;

  return (
    <div>
      {/* Check Summary */}
      <Card className="mb-3 shadow-sm">
        <Card.Body>
          <Row>
            <Col md={4}>
              <small className="text-muted">Transaction Number</small>
              <div className="fw-semibold">{rowData?.transaction_number}</div>
            </Col>
            <Col md={4}>
              <small className="text-muted">Check Number</small>
              <div className="fw-semibold">{rowData?.check_number}</div>
            </Col>
            <Col md={4}>
              <small className="text-muted">Issued Date</small>
              <div className="fw-semibold">{rowData?.transaction_date}</div>
            </Col>
          </Row>

          {/* Check Amount */}
          <Row className="mt-2">
            <Col md={4}>
              <small className="text-muted">Check Amount</small>
              <div className="fw-semibold text-danger">
                {formatToPHP(checkAmount)}
              </div>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* Account Subjects */}
      <Card className="shadow-sm">
        <Card.Body>
          <h6 className="fw-bold mb-3">Account Subjects</h6>
          <Form
            id="issued-check-form"
            noValidate
            onSubmit={handleSubmit(onSubmit)}
          >
            <Row className="g-3">
              <Col md={12}>
                <Form.Group>
                  <Form.Label>Account 1 (Module Type)</Form.Label>
                  <Form.Select
                    {...register("account1", {
                      required: "Module Type is required",
                    })}
                    isInvalid={!!errors.account1}
                  >
                    <option value="" disabled>
                      -- Select --
                    </option>
                    {account1Options.map((moduleType, index) => (
                      <option key={index} value={moduleType}>
                        {moduleType}
                      </option>
                    ))}
                  </Form.Select>
                  <Form.Control.Feedback type="invalid">
                    {errors.account1?.message}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>
            </Row>

            <Row className="g-3 mt-3">
              <Col md={12}>
                <Form.Group>
                  <Form.Label>Account 2 (Subject Name)</Form.Label>
                  <Form.Select
                    {...register("account2", {
                      required: "Subject Name is required",
                    })}
                    disabled={!selectedAccount1}
                    isInvalid={!!errors.account2}
                  >
                    <option value="" disabled>
                      -- Select --
                    </option>
                    {account2Options.map((subject) => (
                      <option key={subject.id} value={subject.id}>
                        {subject.name}
                      </option>
                    ))}
                  </Form.Select>
                  <Form.Control.Feedback type="invalid">
                    {errors.account2?.message}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>
            </Row>

            <Row className="g-3 mt-3">
              <Col md={12}>
                <Form.Group>
                  <Form.Label>Account 3 (Account Name)</Form.Label>
                  <Form.Select
                    {...register("account3", {
                      required: "Account Name is required",
                    })}
                    disabled={!selectedAccount2}
                    isInvalid={!!errors.account3}
                  >
                    <option value="" disabled>
                      -- Select --
                    </option>
                    {account3Options.map((account) => (
                      <option key={account.id} value={account.id}>
                        {account.name}
                      </option>
                    ))}
                  </Form.Select>
                  <Form.Control.Feedback type="invalid">
                    {errors.account3?.message}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>
            </Row>

            {/* Balance Display */}
            {selectedAccountBalance !== null && (
              <Row className="mt-3">
                <Col md={12}>
                  <Alert
                    variant={isInsufficient ? "danger" : "success"}
                    className="mb-0 py-2"
                  >
                    <div className="d-flex justify-content-between align-items-center">
                      <span className="fw-semibold">
                        {isInsufficient
                          ? "⚠ Insufficient Balance"
                          : "✔ Available Balance"}
                      </span>
                      <span className="fw-bold fs-6">
                        {formatToPHP(selectedAccountBalance)}
                      </span>
                    </div>
                    {isInsufficient && (
                      <small className="d-block mt-1">
                        The selected account balance is short by ₱
                        {formatToTwoDecimal(
                          checkAmount - selectedAccountBalance,
                        )}
                      </small>
                    )}
                  </Alert>
                </Col>
              </Row>
            )}
          </Form>
        </Card.Body>
      </Card>
    </div>
  );
};

export default IssuedCheckModalBody;
